import {
  CheckboxGroupField,
  ChildcareSituation,
  DateField,
  EmergencyCategories,
  FieldType,
  LearningSituation,
  Likelihood,
  TextField,
  TransportationSituation,
} from '@bybeam/platform-types';
import { makeEduConfig } from './baseConfig.js';

const CONFIG = makeEduConfig();

CONFIG.introPages[0] = {
  key: 'what-is-it',
  layout: 'welcome',
  title: 'What are Member Support Funds?',
  body: `
Member Support Funds support individuals experiencing a financial challenge or emergency. 
These funds do not require repayment.

If financial assistance could help you pay for your needs, you may be eligible to submit 
an application through {PROGRAM_NAME}.`,
};

const personalInfoSection = CONFIG.sections.find(({ key }) => key === 'personalInfo');
const studentInfoQuestionGroup = personalInfoSection.questionGroups.find(
  ({ key }) => key === 'studentInfo',
);
const studentEmailAddressQuestion = studentInfoQuestionGroup.questions.find(
  ({ key }) => key === 'email',
);
studentEmailAddressQuestion.copy.title = 'What is your email address?';
const studentIdQuestion = studentInfoQuestionGroup.questions.find(({ key }) => key === 'studentId');
studentIdQuestion.copy.title = 'What is your NSPID?';
const studentIdField = studentIdQuestion.fields.find(({ key }) => key === 'studentId') as TextField;
studentIdField.copy = 'NSPID';

const personalInfoQuestionGroup = personalInfoSection.questionGroups.find(
  ({ key }) => key === 'personalInfo',
);
const graduationDateQuestion = personalInfoQuestionGroup.questions.find(
  ({ key }) => key === 'graduationDate',
);
graduationDateQuestion.copy.title =
  'What month and year do you expect to finish your service term?';
const graduationDateField = graduationDateQuestion.fields.find(
  ({ key }) => key === 'graduationDate',
) as DateField;
graduationDateField.copy = 'Expected completion date';

personalInfoQuestionGroup.questions = personalInfoQuestionGroup.questions.filter(
  ({ key }) => key !== 'filedFafsa',
);
personalInfoSection.overview.description = `In this section, you’ll let us know what month and year you expect to finish your service term,
if you receive financial aid and if you’re responsible for taking care of any minors.`;

const financialChallengesSection = CONFIG.sections.find(({ key }) => key === 'financialChallenges');
financialChallengesSection.overview.title =
  "Let's learn more about the financial challenges you are facing during your service term";
const financialChallengesQuestionGroup = financialChallengesSection.questionGroups.find(
  ({ key }) => key === 'financialChallenges',
);
const dropoutLikelihoodQuestion = financialChallengesQuestionGroup.questions.find(
  ({ key }) => key === 'dropOutLikelihood',
);
dropoutLikelihoodQuestion.copy.title =
  'How likely are you to resign from your service position due to your financial emergency?';
dropoutLikelihoodQuestion.fields = [
  {
    type: FieldType.RadioList,
    key: 'dropOutLikelihood',
    options: [
      {
        value: Likelihood.veryLikely,
        label: 'Very likely to resign',
      },
      {
        value: Likelihood.likely,
        label: 'Likely to resign',
      },
      {
        value: Likelihood.neutral,
        label: 'I don’t know',
      },
      {
        value: Likelihood.unlikely,
        label: 'Unlikely to resign',
      },
      {
        value: Likelihood.veryUnlikely,
        label: 'Very unlikely to resign',
      },
    ],
  },
];

const emergencyCategoriesQuestion = financialChallengesQuestionGroup.questions.find(
  ({ key }) => key === 'applicableEmergencyCategories',
);
const emergencyCategoriesQuestionField = emergencyCategoriesQuestion.fields.find(
  ({ key }) => key === 'applicableEmergencyCategories',
) as CheckboxGroupField;
const learningOption = emergencyCategoriesQuestionField.options.find(
  ({ value }) => value === EmergencyCategories.learning,
);
learningOption.label =
  'Professional Development Resources - I.e. unable to access required training, webinars, books, or administrative fees.';

const housingQuestionGroup = financialChallengesSection.questionGroups.find(
  ({ key }) => key === 'housing',
);
const homelessQuestions = housingQuestionGroup.questions.find(
  ({ key }) => key === 'hasBeenHomeless',
);
homelessQuestions.copy.title = 'Since starting your service term, have you ever been homeless?';

const childcareQuestionGroup = financialChallengesSection.questionGroups.find(
  ({ key }) => key === 'childcare',
);
const childcareSituationQuestion = childcareQuestionGroup.questions.find(
  ({ key }) => key === 'childcareSituation',
);
const childcareSituationQuestionField = childcareSituationQuestion.fields.find(
  ({ key }) => key === 'childcareSituation',
) as CheckboxGroupField;
const affordNeedsOption = childcareSituationQuestionField.options.find(
  ({ value }) => value === ChildcareSituation.affordNeeds,
);
affordNeedsOption.label =
  'You were unable or struggled to pay for diapers, formula, or other necessary childcare supplies';

const transportationQuestionGroup = financialChallengesSection.questionGroups.find(
  ({ key }) => key === 'transportation',
);
const transportationSituationQuestion = transportationQuestionGroup.questions.find(
  ({ key }) => key === 'transportationSituation',
);
const transportationSituationField = transportationSituationQuestion.fields.find(
  ({ key }) => key === 'transportationSituation',
) as CheckboxGroupField;
const licenseRevokedOption = transportationSituationField.options.find(
  ({ value }) => value === TransportationSituation.licenseRevoked,
);
licenseRevokedOption.label = 'Your license was revoked or temporarily suspended';

const healthQuestionGroup = financialChallengesSection.questionGroups.find(
  ({ key }) => key === 'health',
);
const physiologicSituationPastMonthQuestion = healthQuestionGroup.questions.find(
  ({ key }) => key === 'physiologicSituationPastMonth',
);
physiologicSituationPastMonthQuestion.copy.title =
  'In the past 30 days, how many days have you felt that emotional, mental, or physical difficulties have hurt your service performance?';
healthQuestionGroup.questions = healthQuestionGroup.questions.filter(
  ({ key }) => key !== 'dacaEligible',
);

const learningQuestionGroup = financialChallengesSection.questionGroups.find(
  ({ key }) => key === 'learning',
);
learningQuestionGroup.name = 'Professional Development';
learningQuestionGroup.overview.title =
  "You selected 'Professional Development' as an area in which you are struggling.";
const learningSituationQuestion = learningQuestionGroup.questions.find(
  ({ key }) => key === 'learningSituation',
);
const learningSituationField = learningSituationQuestion.fields.find(
  ({ key }) => key === 'learningSituation',
) as CheckboxGroupField;
const cannotAffordBooksOption = learningSituationField.options.find(
  ({ value }) => value === LearningSituation.cannotAffordBooks,
);
const cannotAffordSuppliesOption = learningSituationField.options.find(
  ({ value }) => value === LearningSituation.cannotAffordSupplies,
);
cannotAffordBooksOption.label = 'You do not have enough money for required training';
cannotAffordSuppliesOption.label = 'You do not have enough money for helpful training';

const youAndYourSituationSection = CONFIG.sections.find(({ key }) => key === 'youAndYourSituation');
const youAndYourSituationSectionQuestionGroup = youAndYourSituationSection.questionGroups.find(
  ({ key }) => key === 'youAndYourSituation',
);
youAndYourSituationSectionQuestionGroup.questions = [
  CONFIG.definitions.questions.believesFundsWouldHelp,
  ...youAndYourSituationSectionQuestionGroup.questions,
];
CONFIG.sections[CONFIG.sections.findIndex(({ key }) => key === 'documents')] =
  CONFIG.definitions.sections.optionalDocumentation;

export default CONFIG;
